import React, { useContext, useReducer } from "react";
import { GenericActions } from "./actions";
import { genericReducer } from "./reducer";
import {
  ChartTooltipData,
  HoveredTrackpoint,
  initialGenericState,
} from "./state";

export const GenericDispatchContext = React.createContext<
  React.Dispatch<GenericActions>
>(() => undefined);

/*
 * Each value has its own context, so a component rerenders only when the value it reads changes: the chart tooltip
 * and the hovered track point change on every mouse move over the chart or the track line, and e.g. the map
 * container (which reads only whether the 3D map is shown) mustn't rerender then
 */
const ChartTooltipDataContext = React.createContext<ChartTooltipData>(
  initialGenericState.chartTooltipData
);
const HoveredTrackpointContext = React.createContext<HoveredTrackpoint>(
  initialGenericState.hoveredTrackpoint
);
const ShowCesiumMapContext = React.createContext(
  initialGenericState.showCesiumMap
);

export const useGenericDispatch = () => useContext(GenericDispatchContext);
/** The point hovered over the chart, with its tooltip's position and texts */
export const useChartTooltipData = () => useContext(ChartTooltipDataContext);
/** The point of the edited track hovered on the map */
export const useHoveredTrackpoint = () => useContext(HoveredTrackpointContext);
/** Whether the 3D map is shown */
export const useShowCesiumMap = () => useContext(ShowCesiumMapContext);

export const GenericContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(genericReducer, initialGenericState);

  return (
    <ChartTooltipDataContext.Provider value={state.chartTooltipData}>
      <HoveredTrackpointContext.Provider value={state.hoveredTrackpoint}>
        <ShowCesiumMapContext.Provider value={state.showCesiumMap}>
          <GenericDispatchContext.Provider value={dispatch}>
            {children}
          </GenericDispatchContext.Provider>
        </ShowCesiumMapContext.Provider>
      </HoveredTrackpointContext.Provider>
    </ChartTooltipDataContext.Provider>
  );
};
